import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from './contexts/auth.context';
import { useReloadOnNewVersion } from './lib/version-check';
import { usePermissions } from './hooks/use-permissions';
import { portalEntryAllowed, ROUTE_GUARDS } from './config/permissions.config';
import { usePortalViewing } from './stores/portal-view.store';
import { moduleOfPath } from './config/modules.config';
import { useTenantConfig } from './hooks/use-tenant-config';
import { useTenantStore } from './stores/tenant.store';
import { LoginPage } from './pages/auth/login.page';
import { ActivateAccountPage } from './pages/auth/activate-account.page';
import { NoAccessPage } from './pages/auth/no-access.page';
import { AppShell } from './components/layout/app-shell';
import { AuthLoadingScreen } from './components/AuthLoadingScreen';
import { DomainRoleEnforcer } from './components/DomainRoleEnforcer';
import {
  devSimHostname,
  isKnownDosfilosHost,
  resolveDomainTenant,
} from './lib/resolve-tenant-by-domain';
import { PeopleListPage } from './pages/sis/people.page';
import { PersonDetailPage } from './pages/sis/person-detail.page';
import { AdmissionsListPage } from './pages/sis/admissions.page';
import { AdmissionDetailPage } from './pages/sis/admission-detail.page';
import { AdmissionApplicationPage } from './pages/sis/admission-application.page';
import { AcademicPage } from './pages/sis/academic.page';
import { ProgramDetailPage } from './pages/sis/program-detail.page';
import { ProgramLadderPage } from './pages/sis/program-ladder.page';
import { CourseDetailPage } from './pages/sis/course-detail.page';
import { SectionsPage } from './pages/sis/sections.page';
import { ConfigureTermPage } from './pages/sis/configure-term.page';
import { SectionActaPage } from './pages/sis/section-acta.page';
import { TermDetailPage } from './pages/sis/term-detail.page';
import { BulkEnrollPage } from './pages/sis/bulk-enroll.page';
import { StudentsPage } from './pages/sis/students/StudentsPage';
import { CrmContactsPage } from './pages/crm/contacts.page';
import { CrmCampaignsPage } from './pages/crm/campaigns.page';
import { MediaLibraryPage } from './pages/crm/media-library.page';
import { FormTemplatesPage } from './pages/sis/form-templates.page';
import { ApplyPage } from './pages/sis/apply.page';
import { UsersPage } from './pages/admin/users.page';
import { DataAuditPage } from './pages/admin/data-audit.page';
import { TuitionAdminPage } from './pages/sis/tuition-admin.page';
import { PortalShell } from './pages/portal-v2/PortalShell';
import { DashboardPage } from './pages/portal-v2/pages/DashboardPage';
import { CoursesIndexPage } from './pages/portal-v2/pages/CoursesIndexPage';
import { EnrollPage } from './pages/portal-v2/pages/EnrollPage';
import { HistorialRoute } from './pages/portal-v2/pages/HistorialRoute';
import { CourseShell, CourseRedirectToModules } from './pages/portal-v2/pages/CourseShell';
import { CourseModulesPage } from './pages/portal-v2/pages/CourseModulesPage';
import { CourseSessionsPage } from './pages/portal-v2/pages/CourseSessionsPage';
import { CourseAssignmentsPage } from './pages/portal-v2/pages/CourseAssignmentsPage';
import { CourseGradesPage } from './pages/portal-v2/pages/CourseGradesPage';
import { CourseSyllabusPage } from './pages/portal-v2/pages/CourseSyllabusPage';
import { AssignmentDetailPage } from './pages/portal-v2/pages/AssignmentDetailPage';
import { CalendarPage } from './pages/portal-v2/pages/CalendarPage';
import { SurveysListPage } from './pages/portal-v2/pages/SurveysListPage';
import { SurveyDetailPage } from './pages/portal-v2/pages/SurveyDetailPage';
import { ScripturePage } from './pages/portal-v2/pages/ScripturePage';
import { MentorPage } from './pages/portal-v2/pages/MentorPage';
import { SocraticPracticePage } from './pages/portal-v2/pages/SocraticPracticePage';
import { JournalPage } from './pages/portal-v2/pages/JournalPage';
import { PrayerBoardPage } from './pages/portal-v2/pages/PrayerBoardPage';
import { PortalNotificationsSettingsPage } from './pages/portal-v2/pages/NotificationsSettingsPage';
import {
  AnnouncementsPage,
  PaymentsPage,
  ScholarshipsPage,
} from './pages/portal-v2/pages/PlaceholderPages';
import { CareerPage } from './pages/portal-v2/pages/CareerPage';
import { TranscriptPage } from './pages/portal-v2/pages/TranscriptPage';
import { TenantSettingsPage } from './pages/admin/tenant-settings.page';
import { SetupCenterPage } from './pages/setup/setup-center.page';
import { ZoomIntegrationPage } from './pages/admin/zoom-integration.page';
import { MercadoPagoIntegrationPage } from './pages/admin/mercadopago-integration.page';
import { NotificationsSettingsPage } from './pages/settings/notifications-settings.page';
import { ScholarshipsAdminPage } from './pages/sis/scholarships.page';
import { LmsAdminPage } from './pages/sis/lms-admin.page';
import { CourseTemplatesPage } from './pages/sis/course-templates.page';
import { CcdEditorPage } from './pages/cap/ccd-editor.page';
import { ProgramOutcomesPage } from './pages/cap/program-outcomes.page';
import { AlignmentMatrixPage } from './pages/cap/alignment-matrix.page';
import { ComplianceDashboardPage } from './pages/cap/compliance-dashboard.page';
import { CapProposalPage } from './pages/cap/proposal.page';
import { CapJourneyPage } from './pages/cap/cap-journey.page';
import { InstitutionalBoardPage } from './pages/cap/institutional-board.page';
import { InstitutionalCriteriaPage } from './pages/cap/institutional-criteria.page';
import { InstitutionalAttestationsPage } from './pages/cap/institutional-attestations.page';
import { CapHubPage } from './pages/cap/cap-hub.page';
import { TextbookCatalogPage } from './pages/sis/textbook-catalog.page';
import { QuizBuilderPage } from './pages/sis/quiz-builder.page';
import { StandardAuthoringPage } from './pages/sis/standard-authoring.page';
import { SocraticConsolePage } from './pages/sis/socratic-console.page';
import { PracticeProgressPage } from './pages/sis/practice-progress.page';
import { SocraticReviewPage } from './pages/sis/socratic-review.page';
import { GradeAssignmentPage } from './pages/sis/grade-assignment.page';
import { ReportsPage } from './pages/sis/reports.page';
import { SurveysAdminPage } from './pages/sis/surveys-admin.page';
import { AnnouncementsAdminPage } from './pages/sis/announcements-admin.page';
import SeminaryLandingPage from './pages/public/seminary-landing.page';
import ProgramPublicPage, { CustomDomainProgramPage } from './pages/public/program-public.page';
import ApplyPublicPage from './pages/public/apply-public.page';
import { RegisterSeminaryPage } from './pages/public/register-seminary.page';
import { SeminaryRequestsPage } from './pages/admin/seminary-requests.page';
import { PlatformConsolePage } from './pages/admin/platform-console.page';
import { NetworkHomePage } from './pages/network/network-home.page';
import { SubmissionReviewPage } from './pages/network/submission-review.page';
import { CapReviewPage } from './pages/network/cap-review.page';
import { resolverUrl } from './lib/environment';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoadingScreen />;
  if (!user) {
    // Preserve intended URL so login can redirect back
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }
  return (
    <AppShell>
      <ModuleGate>{children}</ModuleGate>
    </AppShell>
  );
}

/** Like ProtectedRoute but skips AppShell — used for pages with custom layout (e.g., student portal). */
function ProtectedRouteRaw({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoadingScreen />;
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }
  return <ModuleGate>{children}</ModuleGate>;
}

// Una página de un módulo que el seminario no usa (modules.config) devuelve al
// inicio —el del portal si se estaba en el portal—. Solo espera la
// configuración del seminario cuando la ruta pertenece a algún módulo: el resto
// de las páginas no se demora.
function ModuleGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const tenantConfig = useTenantConfig();
  const { defaultRoute } = usePermissions();
  if (moduleOfPath(location.pathname) === null) return <>{children}</>;
  if (!tenantConfig.settled) return <AuthLoadingScreen />;
  if (!tenantConfig.pathEnabled(location.pathname)) {
    const home = location.pathname.startsWith('/portal-v2') ? '/portal-v2' : defaultRoute;
    return <Navigate to={home} replace />;
  }
  return <>{children}</>;
}

function RoleGuard({ path, children }: { path: string; children: React.ReactNode }) {
  const { canAccess, permissionsReady, defaultRoute } = usePermissions();
  // "Todavía no sé" NO es "no tenés permiso". `canAccess` es fail-closed, así
  // que sin esta rama redirigiríamos a quien simplemente aún no terminó de
  // cargar. Hoy `ProtectedRoute` ya frena mientras `auth.loading`, así que esto
  // es defensa en profundidad — y lo que evita que el fail-closed se convierta
  // en un rebote prematuro si mañana alguien monta un RoleGuard por fuera.
  if (!permissionsReady) return <AuthLoadingScreen />;
  if (!canAccess(path)) {
    return <Navigate to={defaultRoute} replace />;
  }
  return <>{children}</>;
}

// El portal del alumno: solo con el cargo de alumno, o con una vista «Ver
// portal» abierta (ver `portalEntryAllowed`). Cualquier otra sesión vuelve a su
// inicio en vez de ver un portal «propio» vacío.
function PortalGuard({ children }: { children: React.ReactNode }) {
  const { roles, permissionsReady, defaultRoute } = usePermissions();
  const viewing = usePortalViewing();
  if (!permissionsReady) return <AuthLoadingScreen />;
  if (!portalEntryAllowed({ roles, viewing })) return <Navigate to={defaultRoute} replace />;
  return <>{children}</>;
}

// Handles all unmatched paths:
// 1. Strips trailing slashes first (e.g. /s/sebex/ → /s/sebex) so public routes match correctly.
// 2. Redirects unauthenticated users to login.
// 3. Redirects authenticated users to their role-based home.
function DefaultRedirect() {
  const { user, loading } = useAuth();
  const { availableTenants, currentMembership, currentDomain } = useTenantStore();
  const { defaultRoute } = usePermissions();
  const location = useLocation();

  // Attempt to resolve custom landing domains (e.g. www.sebex.cl). Uses the shared
  // isKnownDosfilosHost (single source of truth — see resolve-tenant-by-domain.ts).
  //
  // `resolving` ARRANCA en true en un dominio propio. Antes arrancaba en false y el
  // fetch corría en un efecto, o sea DESPUÉS del primer render: en una carga en
  // frío daba igual (la sesión todavía estaba cargando), pero al VOLVER al landing
  // navegando (p. ej. desde /programas/pe) la sesión ya estaba lista y ese primer
  // render mandaba al visitante anónimo a /login del dominio de la app.
  const customHost = !isKnownDosfilosHost(devSimHostname() ?? window.location.hostname);
  const [resolving, setResolving] = useState(
    () => location.pathname === '/' && customHost && !currentDomain,
  );
  const [resolvedInfo, setResolvedInfo] = useState<{ slug: string; type?: string } | null>(null);

  useEffect(() => {
    if (location.pathname !== '/') return;
    const hostname = devSimHostname() ?? window.location.hostname;
    if (isKnownDosfilosHost(hostname)) return;

    let cancelled = false;
    (async () => {
      setResolving(true);
      try {
        const res = await fetch(`${resolverUrl()}?domain=${encodeURIComponent(hostname)}`);
        if (!res.ok) return;
        const json = (await res.json()) as { slug?: string };
        if (cancelled) return;
        if (json?.slug) setResolvedInfo({ slug: json.slug, type: (json as any).type });
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // ── Priority 1: strip trailing slash ──────────────────────────────────────
  // Handles /s/sebex/ → /s/sebex so public routes are correctly matched on
  // the next render cycle without triggering the auth redirect below.
  if (location.pathname !== '/' && location.pathname.endsWith('/')) {
    const clean = location.pathname.slice(0, -1);
    return <Navigate to={clean + location.search + location.hash} replace />;
  }

  // ── Priority 2: the landing of a custom domain ─────────────────────────────
  // DomainResolver already resolved this hostname (for every visitor, logged in
  // or not): "/" on a landing domain is the public landing, without waiting for
  // anything else.
  if (location.pathname === '/' && currentDomain?.type === 'landing') {
    return <SeminaryLandingPage slugOverride={currentDomain.slug} />;
  }

  // ── Priority 3: auth redirect ──────────────────────────────────────────────
  if (loading) return null;
  // If we're currently trying to resolve a custom domain, wait.
  if (location.pathname === '/' && resolving) return null;
  // On a LANDING-type custom domain, "/" is ALWAYS the public landing — for both
  // authenticated and anonymous users. The dashboard/portal live on the app
  // (backoffice) domain; DomainRoleEnforcer sends non-public routes there. This
  // is why a logged-in user on sebex.cl sees the landing, not their dashboard.
  if (resolvedInfo?.slug && resolvedInfo.type === 'landing') {
    return <SeminaryLandingPage slugOverride={resolvedInfo.slug} />;
  }
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }
  // Authenticated but linked to no seminary (e.g. fresh Google sign-in).
  if (availableTenants.length === 0) return <Navigate to="/no-access" replace />;
  // Wait for membership to load before redirecting.
  if (currentMembership === null && availableTenants.length > 0) {
    // Esperando la membresía. Antes era `return null` (pantalla en blanco sin
    // salida si nunca llega); ahora la espera es visible y tiene escape.
    return <AuthLoadingScreen />;
  }
  return <Navigate to={defaultRoute} replace />;
}

// Shown at /login: if already logged in, waits for membership then redirects.
// This prevents the flash where an authenticated student briefly sees /admissions
// before the membership is fully loaded and the correct route (/portal) is known.
function LoginOrRedirect() {
  const { user, loading } = useAuth();
  const { availableTenants, currentMembership } = useTenantStore();
  const { defaultRoute } = usePermissions();
  if (loading) return null;
  if (!user) return <LoginPage />;
  if (availableTenants.length === 0) return <Navigate to="/no-access" replace />;
  if (currentMembership === null && availableTenants.length > 0) return <AuthLoadingScreen />;
  return <Navigate to={defaultRoute} replace />;
}

/**
 * Resolves the current custom domain → {tenantId, slug, type} ONCE on mount and
 * stores it as `currentDomain`, INDEPENDENT of auth. DomainRoleEnforcer reads
 * currentDomain.type to decide what a landing domain may serve, so it must be
 * set even for anonymous visitors (otherwise the landing domain would still
 * serve /login and /portal-v2 to logged-out users). resolveDomainTenant() caches
 * per hostname, so this dedupes with auth.context's own resolution.
 */
function DomainResolver(): null {
  const { setCurrentDomain } = useTenantStore();
  useEffect(() => {
    void resolveDomainTenant().then((d) => {
      setCurrentDomain(
        d.mode === 'resolved' ? { tenantId: d.tenantId, slug: d.slug, type: d.type } : null,
      );
    });
  }, [setCurrentDomain]);
  return null;
}

/** Una pestaña abierta se actualiza sola tras un deploy (ver lib/version-check). */
function VersionWatcher() {
  useReloadOnNewVersion();
  return null;
}

export function App() {
  return (
    <>
      <VersionWatcher />
      <DomainResolver />
      <DomainRoleEnforcer />
      <Routes>
        <Route path="/login" element={<LoginOrRedirect />} />
        {/* ── Public routes (no auth required) ── */}
        {/* Seminary landing page — resolved by slug */}
        <Route path="/s/:slug" element={<SeminaryLandingPage />} />
        {/* Public admission application form */}
        <Route path="/s/:slug/apply" element={<ApplyPublicPage />} />
        {/* Public program page: malla + place in the formation path */}
        <Route path="/s/:slug/programas/:code" element={<ProgramPublicPage />} />
        {/* Same page on a seminary's own landing domain (slug from the hostname) */}
        <Route path="/programas/:code" element={<CustomDomainProgramPage />} />
        {/* New URL pattern includes tenantId so the CF doesn't need a collectionGroup query */}
        <Route path="/apply/:tenantId/:token" element={<ApplyPage />} />
        {/* Legacy/fallback: old links without tenantId (collectionGroup query – deprecated) */}
        <Route path="/apply/:token" element={<ApplyPage />} />
        {/* Account activation (public – user arrives from email invite) */}
        <Route path="/auth/activate" element={<ActivateAccountPage />} />
        {/* Authenticated user with no seminary membership */}
        <Route path="/no-access" element={<NoAccessPage />} />
        {/* Public seminary registration (request + approval) */}
        <Route path="/registro" element={<RegisterSeminaryPage />} />

        {/* Platform-admin: review seminary registration requests (self-gates) */}
        <Route
          path="/admin/seminary-requests"
          element={
            <ProtectedRoute>
              <SeminaryRequestsPage />
            </ProtectedRoute>
          }
        />

        {/* Platform-admin: cross-tenant console (self-gates on isPlatformAdmin) */}
        <Route
          path="/plataforma"
          element={
            <ProtectedRoute>
              <PlatformConsolePage />
            </ProtectedRoute>
          }
        />
        {/* Espacio de la red (TMAI). Destino del rol networkAuditor. */}
        <Route
          path="/red"
          element={
            <ProtectedRoute>
              <RoleGuard path="/red">
                <NetworkHomePage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* El expediente de una ronda tiene URL PROPIA: un revisor puede pasárselo
            a otro miembro de la junta, y "atrás" vuelve a la bandeja. Cuelga de
            `/red`, así que `RoleGuard` la cubre con el mismo permiso. */}
        <Route
          path="/red/postulaciones/:submissionId"
          element={
            <ProtectedRoute>
              <RoleGuard path="/red">
                <SubmissionReviewPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />

        {/* Detalle de una ronda de propuesta CAP. Misma protección: vive dentro
            del espacio `/red`, así que `RoleGuard` la cubre con el mismo permiso. */}
        <Route
          path="/red/propuestas/:submissionId"
          element={
            <ProtectedRoute>
              <RoleGuard path="/red">
                <CapReviewPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />

        {/* ── Protected routes ── */}
        <Route
          path="/people"
          element={
            <ProtectedRoute>
              <RoleGuard path="/people">
                <PeopleListPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/people/:id"
          element={
            <ProtectedRoute>
              <RoleGuard path="/people">
                <PersonDetailPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admissions"
          element={
            <ProtectedRoute>
              <RoleGuard path="/admissions">
                <AdmissionsListPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admissions/:id"
          element={
            <ProtectedRoute>
              <RoleGuard path="/admissions">
                <AdmissionDetailPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admissions/:id/application"
          element={
            <ProtectedRoute>
              <RoleGuard path="/admissions">
                <AdmissionApplicationPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/form-templates"
          element={
            <ProtectedRoute>
              <RoleGuard path="/form-templates">
                <FormTemplatesPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/academic"
          element={
            <ProtectedRoute>
              <RoleGuard path="/academic">
                <AcademicPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/academic/anuncios"
          element={
            <ProtectedRoute>
              <RoleGuard path={ROUTE_GUARDS.announcementsAdmin}>
                <AnnouncementsAdminPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/academic/programs/ladder"
          element={
            <ProtectedRoute>
              <RoleGuard path="/academic">
                <ProgramLadderPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/academic/programs/:programId"
          element={
            <ProtectedRoute>
              <RoleGuard path="/academic">
                <ProgramDetailPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/academic/courses/:courseId"
          element={
            <ProtectedRoute>
              <RoleGuard path="/academic">
                <CourseDetailPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sections"
          element={
            <ProtectedRoute>
              <RoleGuard path="/sections">
                <SectionsPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sections/configure/:termId"
          element={
            <ProtectedRoute>
              <RoleGuard path={ROUTE_GUARDS.configureTerm}>
                <ConfigureTermPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sections/acta/:sectionId"
          element={
            <ProtectedRoute>
              <RoleGuard path="/sections">
                <SectionActaPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sections/term/:termId"
          element={
            <ProtectedRoute>
              <RoleGuard path="/sections">
                <TermDetailPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* Alumnos (Gestión): solo con graduation_audit; la página redirige sin él. */}
        <Route
          path="/students"
          element={
            <ProtectedRoute>
              <RoleGuard path="/students">
                <StudentsPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/students/bulk-enroll"
          element={
            <ProtectedRoute>
              <RoleGuard path={ROUTE_GUARDS.bulkEnroll}>
                <BulkEnrollPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* Libro de notas folded into the /curso cockpit as a tab. Keep the path
            as a redirect so old links/bookmarks + the nav entry land on the tab. */}
        <Route path="/grades" element={<Navigate to="/curso?tab=notas" replace />} />
        <Route
          path="/scholarships"
          element={
            <ProtectedRoute>
              <RoleGuard path="/scholarships">
                <ScholarshipsAdminPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* "Material de Clase" was folded into Curso (LMS) → Biblioteca tab.
            Keep the path as a redirect so old links/bookmarks still land. */}
        <Route path="/materiales" element={<Navigate to="/curso" replace />} />
        <Route
          path="/curso"
          element={
            <ProtectedRoute>
              <RoleGuard path="/curso">
                <LmsAdminPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* Section-scoped cockpit — deep-linkable. Same guard as /curso. */}
        <Route
          path="/curso/:sectionId"
          element={
            <ProtectedRoute>
              <RoleGuard path="/curso">
                <LmsAdminPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plantillas-syllabus"
          element={
            <ProtectedRoute>
              <RoleGuard path="/plantillas-syllabus">
                <CourseTemplatesPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* Legacy path → keep old bookmarks/links working. */}
        <Route path="/plantillas-curso" element={<Navigate to="/plantillas-syllabus" replace />} />
        <Route
          path="/cap"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <CapHubPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* La ficha es del CURSO. La ruta por plantilla queda como puerta
            secundaria para no romper enlaces guardados. */}
        <Route
          path="/cap/ficha/:courseId"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <CcdEditorPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/curso/:courseTemplateId/ccd"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <CcdEditorPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/:programId/plos"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <ProgramOutcomesPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/:programId/matriz"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <AlignmentMatrixPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/:programId/cumplimiento"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <ComplianceDashboardPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/:programId/propuesta"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <CapProposalPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/:programId/camino"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <CapJourneyPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/institucional"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <InstitutionalBoardPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/institucional/criterios"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <InstitutionalCriteriaPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cap/institucional/atestaciones"
          element={
            <ProtectedRoute>
              <RoleGuard path="/cap">
                <InstitutionalAttestationsPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/catalogo-libros"
          element={
            <ProtectedRoute>
              <RoleGuard path="/catalogo-libros">
                <TextbookCatalogPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* Asignaciones folded into the /curso cockpit as a tab. Keep the path
            as a redirect so old links/bookmarks + the nav entry land on the tab
            (the section is picked inside the cockpit). */}
        <Route path="/assignments" element={<Navigate to="/curso?tab=asignaciones" replace />} />
        <Route
          path="/estandares"
          element={
            <ProtectedRoute>
              <RoleGuard path="/estandares">
                <StandardAuthoringPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dialogo-socratico"
          element={
            <ProtectedRoute>
              <RoleGuard path="/dialogo-socratico">
                <SocraticConsolePage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/socratic/:assignmentId/review"
          element={
            <ProtectedRoute>
              <RoleGuard path="/dialogo-socratico">
                <SocraticReviewPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/practica-progreso"
          element={
            <ProtectedRoute>
              <RoleGuard path="/practica-progreso">
                <PracticeProgressPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments/quiz/new"
          element={
            <ProtectedRoute>
              <RoleGuard path={ROUTE_GUARDS.quizBuilder}>
                <QuizBuilderPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments/quiz/:assignmentId/edit"
          element={
            <ProtectedRoute>
              <RoleGuard path={ROUTE_GUARDS.quizBuilder}>
                <QuizBuilderPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments/:assignmentId/grade"
          element={
            <ProtectedRoute>
              <RoleGuard path={ROUTE_GUARDS.gradeAssignment}>
                <GradeAssignmentPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/contacts"
          element={
            <ProtectedRoute>
              <RoleGuard path="/crm/contacts">
                <CrmContactsPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/campaigns"
          element={
            <ProtectedRoute>
              <RoleGuard path="/crm/campaigns">
                <CrmCampaignsPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/media"
          element={
            <ProtectedRoute>
              <RoleGuard path="/crm/media">
                <MediaLibraryPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute>
              <RoleGuard path="/usuarios">
                <UsersPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/data-audit"
          element={
            <ProtectedRoute>
              <RoleGuard path="/data-audit">
                <DataAuditPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tuition"
          element={
            <ProtectedRoute>
              <RoleGuard path="/tuition">
                <TuitionAdminPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes"
          element={
            <ProtectedRoute>
              <RoleGuard path="/reportes">
                <ReportsPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/encuestas"
          element={
            <ProtectedRoute>
              <RoleGuard path="/encuestas">
                <SurveysAdminPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/empezar"
          element={
            <ProtectedRoute>
              <RoleGuard path="/empezar">
                <SetupCenterPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <RoleGuard path="/settings">
                <TenantSettingsPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/notifications"
          element={
            <ProtectedRoute>
              <NotificationsSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/zoom"
          element={
            <ProtectedRoute>
              <RoleGuard path="/settings">
                <ZoomIntegrationPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/mercadopago"
          element={
            <ProtectedRoute>
              <RoleGuard path="/settings">
                <MercadoPagoIntegrationPage />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        {/* ── Student portal v1 retired (legacy): redirect everything to v2 ── */}
        <Route path="/portal" element={<Navigate to="/portal-v2" replace />} />
        <Route path="/portal/*" element={<Navigate to="/portal-v2" replace />} />

        {/* ── Student Portal v2 (Canvas-style nested routes) ── */}
        <Route
          path="/portal-v2"
          element={
            <ProtectedRouteRaw>
              <PortalGuard>
                <PortalShell />
              </PortalGuard>
            </ProtectedRouteRaw>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="enroll" element={<EnrollPage />} />
          <Route path="courses" element={<CoursesIndexPage />} />
          <Route path="courses/:sectionId" element={<CourseShell />}>
            <Route index element={<CourseRedirectToModules />} />
            <Route path="modules" element={<CourseModulesPage />} />
            <Route path="clases" element={<CourseSessionsPage />} />
            <Route path="assignments" element={<CourseAssignmentsPage />} />
            <Route path="assignments/:assignmentId" element={<AssignmentDetailPage />} />
            <Route path="grades" element={<CourseGradesPage />} />
            <Route path="syllabus" element={<CourseSyllabusPage />} />
          </Route>
          <Route path="surveys" element={<SurveysListPage />} />
          <Route path="surveys/:surveyId" element={<SurveyDetailPage />} />
          <Route path="career" element={<CareerPage />} />
          <Route path="historial" element={<HistorialRoute />} />
          <Route path="historial/transcript" element={<TranscriptPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="scripture" element={<ScripturePage />} />
          <Route path="journal" element={<JournalPage />} />
          <Route path="prayer" element={<PrayerBoardPage />} />
          <Route path="mentor" element={<MentorPage />} />
          <Route path="practica-socratica" element={<SocraticPracticePage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="scholarships" element={<ScholarshipsPage />} />
          <Route path="settings/notifications" element={<PortalNotificationsSettingsPage />} />
        </Route>

        {/* Default redirect → role-based home */}
        <Route path="*" element={<DefaultRedirect />} />
      </Routes>
    </>
  );
}
